import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { showSuccess, showApiError } from "@/lib/toast";
import {
  addChannelDetailContent,
  addChannelDetailDate,
  addChannelDetailItem,
  getChannelDetailItems,
  getChannelDetails,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { ChannelCalendarItemMetaForm, FormSchemaType } from "./ChannelCalendarItemMetaForm";
import { convertTimeToUtc, formatToUTC } from "@/lib/dateUtils";
import { addDays } from "date-fns";

type Props = {
  onOpenChange: (open: boolean) => void;
};

export const ChannelCalendarItemAddForm: React.FC<Props> = ({ onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [queryParams] = useSearchParams();
  const channelId = queryParams.get("Id");

  const { data: channelDetails } = useQuery({
    queryFn: () => getChannelDetails(String(channelId), String(activeFirm?.firmGuid)),
    queryKey: ["getChannelDetails", channelId],
  });

  const channelDetailsData = channelDetails?.data;

  const queryClient = useQueryClient();
  const addEventMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      if (values.periodEnd === values.periodStart) {
        values.periodEnd = addDays(formatToUTC(values.periodEnd), 1).toISOString();
      } else {
        values.periodEnd = formatToUTC(values.periodEnd);
      }

      const channelDetailItem = await addChannelDetailItem({
        channelId: Number(channelDetailsData?.intId),
        periodStart: formatToUTC(values.periodStart),
        periodEnd: values.periodEnd,
        isActive: values.isActive,
        firmId: String(activeFirm?.firmGuid),
      });

      const channelDetailItems = await getChannelDetailItems(String(channelId), String(activeFirm?.firmGuid));

      const channelDetailItemsData = channelDetailItems?.data.result || [];
      const newChannelDetailItem = channelDetailItemsData.find((item) => item.id === channelDetailItem.data.newDataId);

      for (const content of values.contentList) {
        if (newChannelDetailItem?.intId) {
          await addChannelDetailContent({
            channelDetailId: newChannelDetailItem.intId,
            contentId: content.contentId || undefined,
            contentGroupId: content.contentGroupId || undefined,
            firmGuid: String(activeFirm?.firmGuid),
          });
        }
      }

      for (const day of values.weekDays) {
        await addChannelDetailDate({
          channelDetailId: Number(newChannelDetailItem?.intId),
          firmGuid: String(activeFirm?.firmGuid),
          dayOfWeek: day.dayOfWeek,
          startTime: convertTimeToUtc(day.startTime),
          endTime: convertTimeToUtc(day.endTime),
        });
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getChannelDetailItems"] });
      showSuccess(t("common.addedEventSuccess"));
      onOpenChange(false);
    },
    onError: showApiError,
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[70vw] max-w-[100vw]">
        <DialogHeader>
          <DialogTitle>{t("channel.newEvent")}</DialogTitle>
          <DialogDescription>
            <ChannelCalendarItemMetaForm
              buttonText={t("common.save")}
              onSubmit={(values) => addEventMutation.mutate(values)}
              isLoading={addEventMutation.isPending}
              defaultScreenSizeGuid={channelDetailsData?.screenSizeId}
            />
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
